import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Box, Button, Link, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import request from 'api'
import { Loading } from 'components/ui'
import { useDateTime, useMappedTranslation } from 'hooks'
import { getGlobalConsentApi } from 'pages/policies/api'
import { Consent, appendLegacyParams, buildPolicySearch } from 'pages/policies/utils'
import Detail from 'pages/policies/views/detail'
import i18n from 'utils/i18n'

import { AutomationTagIds } from '../../../assets/test/test-automation-tag-id'

export default function TermsConditions() {
  const t = useMappedTranslation()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [detail, setDetail] = useState<string>('')
  const [latestConsent, setLatestConsent] = useState<Consent>()
  const dateTime = useDateTime()
  const theme = useTheme()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const paramCountryCode = searchParams.get('countryCode') || undefined
  const paramLocale = searchParams.get('locale') || undefined
  const policySearch = buildPolicySearch(searchParams)

  const getDetailApi = (uri: string) => {
    const LEGACY_HOST = 'https://account.samsung.com'
    return request
      .get(uri)
      .then((res) => {
        if (res.status !== 200) {
          throw new Error('Failed getConsentBody')
        }

        const document = new DOMParser().parseFromString(res.data, 'text/html')

        const language = paramLocale?.replace('-', '_') || i18n.language?.replace('-', '_')
        if (language) {
          document.querySelectorAll('a').forEach((tag) => {
            if (tag.href.includes(`${LEGACY_HOST}`)) {
              tag.href = appendLegacyParams(tag.href, language, paramCountryCode)
            }
          })
        }

        const body = document.getElementsByTagName('body').item(0)?.innerHTML
        if (body !== undefined) {
          const sanitized = body.replace(
            // due to KOR terms html include body padding set.
            'body { word-wrap:break-word; word-break:keep-all; padding:1.5em; }',
            'body { word-wrap:break-word; word-break:keep-all; }'
          )
          setDetail(sanitized)
        }
      })
      .catch(() => setDetail('<div>Failed to load</div>'))
  }

  const handlePolicyService = () => {
    navigate(`/policies/terms-conditions/services${policySearch}`)
  }

  const handlePoliciesTermConditions = () => {
    navigate(`/policies/terms-conditions${policySearch}`)
  }

  const renderAction = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          pt: { xs: '52px', md: '72px' }
        }}
      >
        <Typography
          className='off'
          fontSize='13px'
          lineHeight='17px'
          fontWeight='700'
          color={theme.palette.mode === 'dark' ? '#999999' : '#636363'}
        >
          {t('iam.01.Common.Txt.Effective.date', { 0: dateTime(Number(latestConsent?.publish), { format: 'withoutTime' }) })}
        </Typography>
        <Button
          sx={{
            mt: '12px',
            padding: '7px 12px',
            fontSize: '13px',
            lineHeight: '19px',
            minHeight: '0',
            textTransform: 'none',
            color: 'text.primary',
            textDecoration: 'none',
            border: `0.5px solid #636363`,
            borderRadius: '24px',
            minWidth: { xs: '152px' },
            display: 'block',
            height: '33px',
            fontWeight: 500
          }}
          onClick={handlePoliciesTermConditions}
          data-testid='Link1942CEDE'
        >
          {t('iam.01.Common.Notice.detail.list.01')}
        </Button>
        <Link
          component='button'
          type='button'
          onClick={handlePolicyService}
          color='text.secondary'
          sx={{
            cursor: 'pointer',
            mt: '24px',
            fontSize: '16px',
            fontHeight: '24px',
            fontWeight: '700',
            width: '324px',
            textAlign: 'center'
          }}
          data-testid='Link2857B31F'
        >
          {t('policy.service.title')}
        </Link>
      </Box>
    )
  }

  useEffect(() => {
    setIsLoading(true)
    getGlobalConsentApi('TC', false, { overrides: { countryCode: paramCountryCode, locale: paramLocale } })
      .then((res) => {
        if (res.status !== 200) {
          throw new Error('Failed getConsentApi')
        } else {
          return res.data[0] as Consent
        }
      })
      .then((data) => {
        setLatestConsent(data)
        return getDetailApi(data.uri)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [paramCountryCode, paramLocale])

  return (
    <Box sx={{ width: '100%', height: '100%', padding: { xs: '24px', md: '0' }, paddingTop: { xs: '5px', md: '46px' } }}>
      {isLoading ? (
        <Loading fullScreen />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: { xs: '100%', md: '1040px' } }} data-testid={AutomationTagIds['policies.tnc.page']}>
            <Detail
              title={t('iam.01.Common.Terms.and.conditions.Title')}
              isLoading={isLoading}
              detail={detail}
              action={renderAction()}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}
